import { getUnauthorizedHandler, getBaseUrl, getHeaders } from '@Utils/core/api/apiConfiguration';

function getUrlDefault(url: string) {
  return `${getBaseUrl()}${url}`;
}

async function onUnauthorizedError() {
  const handler = getUnauthorizedHandler();
  if (handler) {
    try {
      await handler();
    } catch (e) {
      console.error(e);
    }
  }
}

export interface Options {
  signal?: RequestInit['signal'];
  method?: string;
  headers?: HeadersInit;
  body?: any;
  mode?: 'cors' | 'no-cors';
  data?: Record<string, any> | string;
}

function getBaseConfig(
  options: Options = {
    headers: {},
  },
) {
  const config: RequestInit = {
    method: 'GET',
    credentials: 'omit',
    ...options,
    headers: {
      ...getHeaders(),
      ...options.headers,
    },
  };

  if (options.data) {
    config.body = JSON.stringify(options.data);
    if (config.headers) {
      // @ts-expect-error -- TS doesn't know that headers is defined
      config.headers['Content-Type'] = 'application/json';
    }
  }

  return config;
}

export type Decorator<Type extends RequestInit = {}> = (config: Type) => Type;
const defaultDecorator: Decorator = (c) => c;

export type Api = <T = any>(url: string, options?: Options) => Promise<T>;

type CreateApi = (configDecorator?: Decorator<any>, getUrl?: (url: string) => string) => Api;

function throwResponse(responseBodyParsed: ParsedResponse, requestFailInfo: string): never {
  if (typeof responseBodyParsed === 'object') {
    const error = {
      ...responseBodyParsed,
      message: responseBodyParsed.message ? responseBodyParsed.message : requestFailInfo,
      requestFailInfo,
    };
    console.error(error);
    throw error;
  }

  const error = new Error(`${requestFailInfo} ${responseBodyParsed}`);
  console.error(error);
  throw error;
}

// We maked sure, that if request fails it will have message property in it
async function handleResponse(res: Response): Promise<any> {
  const { status, statusText } = res;

  let responseBodyParsed: ParsedResponse = '';
  try {
    responseBodyParsed = await parseResponse(res);
  } catch (parseError) {
    if (parseError.name === 'AbortError') {
      throw parseError;
    }
    const responseText = await res.text();
    throw new Error(`Response parsing error! Response: ${responseText}`);
  }

  const firstDigitOfStatus = parseInt(`${status}`[0], 10);

  if (firstDigitOfStatus === 2) {
    return responseBodyParsed;
  }

  const requestFailureInformation = `${status}: ${statusText}`;

  if (firstDigitOfStatus === 5) {
    return throwResponse(responseBodyParsed, requestFailureInformation);
  }

  if (status === 401) {
    await onUnauthorizedError();
  }

  return throwResponse(responseBodyParsed, requestFailureInformation);
}

const createApi: CreateApi = (configDecorator = defaultDecorator, getUrl = getUrlDefault) => {
  return (url, options = {}) =>
    fetch(getUrl(url), configDecorator(getBaseConfig(options))).then(handleResponse);
};

type ParsedResponseJson = Record<string, any>;
type ParsedResponse = string | ParsedResponseJson;

function parseResponse(response: Response): Promise<ParsedResponse> {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return response.json();
  }
  return response.text();
}

export default createApi;
